// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import logo from '@/assets/logo.png';
import { AuthenticationSSOProvider } from '@/authentication/api/types';
import { MagicLinkForm } from '@/authentication/components/magic-link-form';
import { PasswordLoginForm } from '@/authentication/components/password-login-form';
import { useLogin } from '@/authentication/hooks/use-login';
import { InternalLink } from '@/components/link';
import { GoogleLogo } from '@/icons/google-logo';
import { MicrosoftLogo } from '@/icons/microsoft-logo';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

const MARKETING_URL = 'https://www.hardfin.com/';
const PRIVACY_POLICY_URL = 'https://www.hardfin.com/legal/privacy-policy';
const TERMS_OF_SERVICE_URL = 'https://www.hardfin.com/legal/terms-of-service';
const isDevMode = import.meta.env.MODE === 'development';
const searchSchema = z.object({
  redirect: z.string().optional(),
  magiclink: z.boolean().optional(),
  link: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  component: LoginRoute,
  validateSearch: searchSchema,
});

function LoginRoute() {
  const search = Route.useSearch();
  const { launchAuthentication: login, error: loginError } = useLogin(
    search.redirect
  );

  const createClickHandler = (provider: AuthenticationSSOProvider) => () => {
    login(provider);
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns="1fr"
      gridTemplateRows="1fr auto"
      alignItems="center"
      justifyContent="center"
      bgcolor="grey.50"
      width="100dvw"
      height="100dvh"
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={16}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack padding={4} minWidth="30rem" alignItems="center">
            <Stack
              paddingY={4}
              paddingX={3}
              spacing={3}
              alignItems="center"
              width="50%"
              minWidth="20rem"
            >
              <Link href={MARKETING_URL}>
                <img alt="logo" src={logo} height="40px" width="auto" />
              </Link>
              <Typography
                color="text.secondary"
                variant="body2"
                paddingBottom={1}
              >
                Access the Hardfin platform
              </Typography>
              {loginError && <Alert severity={'error'}>{loginError}</Alert>}
              <Button
                fullWidth
                variant="outlined"
                onClick={createClickHandler('google')}
                startIcon={<GoogleLogo />}
              >
                Sign in with Google
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={createClickHandler('azureadv2')}
                startIcon={<MicrosoftLogo />}
              >
                Sign in with Microsoft
              </Button>
              {isDevMode && <PasswordLoginForm redirectURL={search.redirect} />}
              {search.magiclink && (
                <MagicLinkForm
                  linkID={search.link}
                  redirectURL={search.redirect}
                />
              )}
            </Stack>
            <Divider sx={{ marginY: 3, minWidth: '100%' }} />
            <Grid container>
              <Grid item xs={4}>
                <Typography align="left">
                  <Link
                    target="_blank"
                    href="mailto:support@hardfin.com"
                    color="text.secondary"
                    variant="body2"
                  >
                    Need help?
                    <br />
                    Email us.
                  </Link>
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography align="right">
                  <InternalLink
                    to="/register"
                    color="text.secondary"
                    variant="body2"
                  >
                    Don&apos;t have an account?
                    <br />
                    Register here.
                  </InternalLink>
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </Container>
      <Stack
        direction="row"
        marginLeft="auto"
        marginRight={{ xs: 'auto', sm: 2 }}
        marginBottom={2}
        spacing={1}
      >
        <Link
          rel="noopener"
          target="_blank"
          variant="caption"
          href={PRIVACY_POLICY_URL}
        >
          Privacy policy
        </Link>
        <Link
          rel="noopener"
          target="_blank"
          variant="caption"
          href={TERMS_OF_SERVICE_URL}
        >
          Terms of service
        </Link>
      </Stack>
    </Box>
  );
}
